'use client';

import { useTranslation } from '@/hooks/useTranslation';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import { FiVideo, FiZap } from 'react-icons/fi';
import { BiDollar, BiRocket, BiTime, BiTrendingUp } from 'react-icons/bi';
import { HiOutlineChartBar, HiOutlineClock, HiOutlineRocketLaunch } from 'react-icons/hi2';
import { PiRobotDuotone } from 'react-icons/pi';
import { HeroBots, IImageOrientation } from '@/components/blocks/HeroBots';
import WhyChooseThisBot, { ColorDefaultChoice } from '@/components/blocks/WhyChooseThisBot';
import TopThreeBenefits from '@/components/blocks/TopThreeBenefits';
import JoinToPartner from '@/components/blocks/JoinToPartner';
import BlockDescriptionImageRight from '@/components/blocks/BlockDescriptionImageRight';
import { RiSparkling2Line } from 'react-icons/ri';
import BlockDescriptionImageLeft from '@/components/blocks/BlockDescriptionImageLeft';
import SiteFrequentlyAskedQuestionsComponent from '@/components/frequently-asked-questions/SiteFrequentlyAskedQuestionsComponent';
import React from 'react';
import { CtaTargetType } from '@/components/buttons/LouiseButton';
import { MdVerified } from 'react-icons/md';
import Testimonial from '@/components/testimonial/Testimonial';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  const hero = {
    content: {
      badge: t('Agencies/banner/agencies'),
      title: t('Agencies/banner/title'),
      description: t('Agencies/banner/description'),
      cta: t('Agencies/banner/cta'),
      trustedBy: t('Agencies/banner/trusted-by'),
      img: 'https://cdn.1cdn.app/application/LOUISEBOT/25071611385671_allbots_2.png',
      icon: AiOutlineThunderbolt,
      imgOrientation: 'landscape' as IImageOrientation,
      ctaTarget: 'register' as CtaTargetType,
    },
    benefits: [
      { icon: <AiOutlineThunderbolt className="tw-w-5 tw-h-5" />, text: t('Agencies/banner/benefits/benefit1') },
      { icon: <BiDollar className="tw-w-5 tw-h-5" />, text: t('Agencies/banner/benefits/benefit2') },
    ],
    stats: [
      { value: `6 ${t('Agencies/banner/weeks')}`, label: t('Agencies/banner/stats/stats1') },
      { value: '20 min', label: t('Agencies/banner/stats/stats2') },
    ],
  };

  const sections = t('Agencies/whychoose/sections', { returnObjects: true }) as Array<any>;

  const whyChooseThisBot = [
    {
      iconicText: t(sections[0].improvement),
      title: t(sections[0].title),
      description: t(sections[0].description),
      icon: <BiTrendingUp className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineChartBar className="tw-w-6 tw-h-6 tw-text-purple-600" />,
      metricLabel: t(sections[0].metricLabel),
      button: t(sections[0].button),
      metric: t(sections[0].metric),
      subtext: t(sections[0].subtext),
      colorFrom: 'pink' as ColorDefaultChoice,
      colorTo: 'purple' as ColorDefaultChoice,
    },
    {
      iconicText: t(sections[1].improvement),
      title: t(sections[1].title),
      description: t(sections[1].description),
      icon: <BiTime className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineClock className="tw-w-6 tw-h-6 tw-text-blue-600" />,
      metricLabel: t(sections[1].metricLabel),
      button: t(sections[1].button),
      metric: t(sections[1].metric),
      subtext: t(sections[1].subtext),
      colorFrom: 'blue' as ColorDefaultChoice,
      colorTo: 'cyan' as ColorDefaultChoice,
    },
    {
      iconicText: t(sections[2].improvement),
      title: t(sections[2].title),
      description: t(sections[2].description),
      icon: <BiRocket className="tw-w-10 tw-h-10 tw-text-white" />,
      outlineIcon: <HiOutlineRocketLaunch className="tw-w-6 tw-h-6 tw-text-green-600" />,
      metricLabel: t(sections[2].metricLabel),
      button: t(sections[2].button),
      metric: t(sections[2].metric),
      subtext: t(sections[2].subtext),
      colorFrom: 'green' as ColorDefaultChoice,
      colorTo: 'emerald' as ColorDefaultChoice,
    },
  ];

  const topThreeFeature = {
    content: {
      title: t('Agencies/ChooseYourApproach/title'),
      subtitle: t('Agencies/ChooseYourApproach/subtitle'),
      badge: t('Agencies/ChooseYourApproach/badge'),
    },
    features: [
      {
        icon: FiZap,
        title: t('Agencies/ChooseYourApproach/feature1'),
        description: t('Agencies/ChooseYourApproach/feature1-2'),
        button: t('Agencies/ChooseYourApproach/feature1-3'),
        imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/24022709580073_Home_2.png',
        color: 'blue',
        gradient: true,
        metric: t('Agencies/ChooseYourApproach/feature1-metric'),
        metricLabel: '3x',
        ctaTarget: 'register' as CtaTargetType,
      },
      {
        icon: PiRobotDuotone,
        title: t('Agencies/ChooseYourApproach/feature2'),
        description: t('Agencies/ChooseYourApproach/feature2-2'),
        button: t('Agencies/ChooseYourApproach/feature2-3'),
        imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/24022709580054_Home_4.png',
        color: 'purple',
        gradient: true,
        metric: t('Agencies/ChooseYourApproach/feature2-metric'),
        metricLabel: 'Up to 80% (12 months)',
        ctaTarget: 'register' as CtaTargetType,
      },
      {
        icon: FiVideo,
        title: t('Agencies/ChooseYourApproach/feature3'),
        description: t('Agencies/ChooseYourApproach/feature3-2'),
        button: t('Agencies/ChooseYourApproach/feature3-3'),
        imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/2402270958004_Home_3.png',
        color: 'green',
        gradient: true,
        metric: t('Agencies/ChooseYourApproach/feature3-metric'),
        metricLabel: '2-4x',
        ctaTarget: 'register' as CtaTargetType,
      },
    ],
  };

  return (
    <div>
      {/* Hero Section - Full Width */}
      <HeroBots benefits={hero.benefits} stats={hero.stats} content={hero.content} locale={locale} />

      <AnimatedGradientWrapper>
        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <WhyChooseThisBot
            title={t('Agencies/whychoose/title')}
            subtitle={t('Agencies/whychoose/subtitle')}
            description={t('Agencies/whychoose/description')}
            sections={whyChooseThisBot}
          />

          <TopThreeBenefits
            title={topThreeFeature.content.title}
            subtitle={topThreeFeature.content.subtitle}
            badge={topThreeFeature.content.badge}
            features={topThreeFeature.features}
            locale={locale}
          />
        </section>

        {/* Testimonal Section */}
        <Testimonial locale={locale} />

        <section className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
            <div className="tw-space-y-32">
              <BlockDescriptionImageRight
                badge={t('Agencies/block1/badge')}
                badgeIcon={<RiSparkling2Line className="tw-w-4 tw-h-4" />}
                title={t('Agencies/block1/title')}
                description={t('Agencies/block1/description')}
                btnColor="blue"
                cta={t('Agencies/block1/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/25071719245013_credit_upsell.png'}
                link="register"
                locale={locale}
              />
              <BlockDescriptionImageLeft
                title={t('Agencies/block2/title')}
                description={t('Agencies/block2/description')}
                btnColor="blue"
                cta={t('Agencies/block2/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/24111612342130_white_label.png'}
                link="register"
                locale={locale}
              />
              <BlockDescriptionImageRight
                badgeIcon={<MdVerified className="tw-w-4 tw-h-4" />}
                title={t('Agencies/block3/title')}
                description={t('Agencies/block3/description')}
                btnColor="blue"
                cta={t('Agencies/block3/cta')}
                image={'https://cdn.1cdn.app/application/LOUISEBOT/24111612342054_custom_label.png'}
                link="register"
                locale={locale}
              />
            </div>
          </div>
        </section>

        <section className="tw-mt-10">
          <JoinToPartner locale={locale} />
        </section>

        <section className="tw-mt-20">
          <SiteFrequentlyAskedQuestionsComponent session="AgenciesFAQs" locale={locale} />
        </section>
      </AnimatedGradientWrapper>
    </div>
  );
}
